import { Box, useMediaQuery } from '@mui/material';
import useDownloadGuide from '../hooks/useDownloadGuide';
import { useNavigate } from 'react-router-dom';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import PrimaryBlueButton from '../components/common/buttons/PrimaryBlueButton';
import BorderedButton from '../components/common/buttons/BorderedButton';
import { useEffect, useState } from 'react';
import { useLatestReport } from '../utils/reports';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

const backendURL = process.env.REACT_APP_BACKEND_URL;

interface ResultCTAOptionsProps {
  showUnderstandTestButton?: boolean;
  showLatestResultButton?: boolean;
  testId: number;
}

export default function ResultCTAOptions({
  showUnderstandTestButton = false,
  showLatestResultButton = false,
  testId,
}: ResultCTAOptionsProps) {
  const guideDownloadUrl = `${backendURL}/files/result-guide?test_id=${encodeURIComponent(testId)}`;
  const guideName = 'result-guide.pdf';
  const { isGuideDownloading, error, handleDownloadClick } = useDownloadGuide(guideDownloadUrl, guideName);
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [reportData, setReportData] = useState<any>();
  const { latestReportLoading, fetchLatestReport } = useLatestReport();

  useEffect(() => {
    const fetchLatestReportsFunction = async () => {
      if (!showLatestResultButton) return;

      let latestReportResponse = await fetchLatestReport();
      if (!latestReportResponse.message && latestReportResponse.latestReport) {
        setReportData(latestReportResponse.latestReport);
      }
    };
    fetchLatestReportsFunction();
  }, []);

  const renderDownloadGuideBtn = () => {
    if (isGuideDownloading) {
      return (
        <GradientCircularProgress
          loadingMessage=""
          additionalCircularSxProps={{
            width: '30px !important',
            height: '30px !important',
            marginTop: '-14px',
            mr: '10px',
          }}
        />
      );
    }

    if (error) {
      return <></>;
    }

    return (
      <PrimaryBlueButton
        sxProps={{ minWidth: '210px' }}
        onClick={handleDownloadClick}
        additionalProps={{
          startIcon: (
            <img
              src={theme.custom.report.resultCTAOptions.primaryBlueButton.imageIcon}
              alt="report download"
              style={{
                marginRight: '8px',
                verticalAlign: 'middle',
              }}
            />
          ),
        }}
        buttonText="Download guide"
      />
    );
  };

  const understandYourTestBtn = () => {
    if (!showUnderstandTestButton) return <></>;

    return (
      <BorderedButton
        sxProps={{
          display: 'flex',
          padding: '10px 16px 10px 16px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4px',
          alignSelf: 'stretch',
          width: { xs: '100%', sm: '210px' },
          '& .MuiButton-startIcon': {
            height: '18px',
            width: '18px',
            backgroundImage: `url(${theme.custom.report.resultCTAOptions.understandYourTest.defaultIcon})`,
          },
          '&:hover': {
            background: 'transparent',
            color: theme.buttons.ghost.hover.color,
            border: '2px solid',
            '& .MuiButton-startIcon': {
              height: '18px',
              width: '18px',
              backgroundImage: `url(${theme.custom.report.resultCTAOptions.understandYourTest.hoverIcon})`,
            },
          },
        }}
        additionalProps={{
          style: { borderRadius: '100px', height: '48px' },
          // Needed so the icon styles work as expected
          startIcon: <></>,
        }}
        onClick={() => navigate('/pfas-info.html')}
        buttonText="Understand your Test"
      />
    );
  };

  const renderViewLatestResultBtn = () => {
    if (!showLatestResultButton || isMobile) return <></>;

    if (latestReportLoading) {
      return (
        <GradientCircularProgress
          loadingMessage=""
          additionalCircularSxProps={{
            width: '30px !important',
            height: '30px !important',
            marginTop: '-14px',
            mr: '10px',
          }}
        />
      );
    }

    if (reportData) {
      return (
        <BorderedButton
          sxProps={{
            display: 'flex',
            padding: '10px 24px 10px 16px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'stretch',
            minWidth: { sm: '240px' },
            width: { xs: '100%', sm: '250px' },
            '& .MuiButton-startIcon': {
              height: '18px',
              width: '18px',
              backgroundImage: `url(${theme.custom.report.resultCTAOptions.viewYourLatestResults.defaultIcon})`,
            },
            '&:hover': {
              background: 'transparent',
              color: theme.buttons.ghost.hover.color,
              border: '2px solid',
              '& .MuiButton-startIcon': {
                height: '18px',
                width: '18px',
                backgroundImage: `url(${theme.custom.report.resultCTAOptions.viewYourLatestResults.hoverIcon})`,
              },
            },
          }}
          additionalProps={{
            style: { borderRadius: '100px', height: '48px' },
            // Needed so the icon styles work as expected
            startIcon: <></>,
          }}
          onClick={() => {
            window.scrollTo(0, 0);
            navigate(`/report/${reportData.centre_report_id}`);
          }}
          buttonText="View your latest results"
        />
      );
    } else return <></>;
  };

  return (
    <Box
      sx={{
        mt: 2,
        display: 'flex',
        gap: '16px',
        width: { xs: '100%', sm: 'auto', md: 'auto' },
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      {renderDownloadGuideBtn()}
      {understandYourTestBtn()}
      {renderViewLatestResultBtn()}
    </Box>
  );
}
